// Components
import {
  AppLayout,
  BreadcrumbGroup,
  ContentLayout,
  Header,
  Tabs,
} from '@amzn/awsui-components-react';
import { SignupList } from './signupsList';
import { ActivitySidebar } from './sideNav';
// Hooks
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
// Utils
import {
  DISPLAY_ATTENDANCE_INFO,
  DISPLAY_DATE_INFO,
  DISPLAY_LOCATION_INFO,
} from '../shifts/shiftInfo';
import { useRef, useState } from 'react';
import { PAGE_KEY } from '../../hooks/attendance/urlPage';
import { WaitlistsTab } from './waitlistsTab';
import { WaitlistInfoHelpPanel } from './waitlistInfoHelpPanel';
import { featureIsEnabled, useFeatures } from '../../hooks/features';
import { SHIFT_WAITLIST } from '../../utils/constants';

type TabType = 'upcoming' | 'past' | 'waitlists';

export function ActivityPage() {
  const { t } = useTranslation(['activity', 'translation']);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const features = useFeatures();
  const isWaitlistEnabled = featureIsEnabled(SHIFT_WAITLIST, features, searchParams);

  const tabParam = searchParams.get('tab') as TabType | null;
  const validTabs: TabType[] = isWaitlistEnabled
    ? ['upcoming', 'past', 'waitlists']
    : ['upcoming', 'past'];
  const activeTabId: TabType = tabParam && validTabs.includes(tabParam) ? tabParam : 'upcoming';

  const urlFeatures = searchParams.get('features');
  const priorityShiftId = searchParams.get('shiftId') ?? undefined;
  const volunteerActivityUrl = `/my-activity${urlFeatures ? '?features=' + urlFeatures : ''}`;
  const now = useRef(new Date()).current;
  const [waitlistInfoOpen, setWaitlistInfoOpen] = useState(false);

  const renderTabs = () => (
    <Tabs
      activeTabId={activeTabId}
      onChange={({ detail }) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('tab', detail.activeTabId);
        // Reset pagination when switching tabs so each tab has its own page state.
        newParams.delete(PAGE_KEY);
        setSearchParams(newParams);
        // Close the Waitlists info panel when leaving the tab.
        setWaitlistInfoOpen(false);
      }}
      tabs={[
        {
          label: t('upcoming'),
          id: 'upcoming',
          content: (
            <SignupList
              startDate={now}
              sort="oldest-first"
              displayAttributes={[DISPLAY_DATE_INFO, DISPLAY_LOCATION_INFO]}
              priorityShiftId={priorityShiftId}
            />
          ),
        },
        {
          label: t('past'),
          id: 'past',
          content: (
            <SignupList
              endDate={now}
              sort="newest-first"
              displayAttributes={[
                DISPLAY_DATE_INFO,
                DISPLAY_LOCATION_INFO,
                DISPLAY_ATTENDANCE_INFO,
              ]}
              includeAttendanceOnlyShifts={true}
            />
          ),
        },
        ...(isWaitlistEnabled
          ? [
              {
                label: t('waitlists'),
                id: 'waitlists',
                content: <WaitlistsTab onOpenInfo={() => setWaitlistInfoOpen(true)} />,
              },
            ]
          : []),
      ]}
    />
  );

  const getContent = () => {
    return (
      <div className="page-content">
        <ContentLayout
          header={
            <div className="page-content-header">
              <Header variant="h1">{t('pageTitle.volunteerActivity')}</Header>
            </div>
          }
        >
          {renderTabs()}
        </ContentLayout>
      </div>
    );
  };

  return (
    <AppLayout
      footerSelector=".footer"
      navigationHide={false}
      navigation={<ActivitySidebar baseUrl={volunteerActivityUrl} />}
      toolsHide={activeTabId !== 'waitlists'}
      toolsOpen={waitlistInfoOpen}
      onToolsChange={({ detail }) => setWaitlistInfoOpen(detail.open)}
      tools={<WaitlistInfoHelpPanel />}
      breadcrumbs={
        <div className="breadcrumbs">
          <BreadcrumbGroup
            items={[
              { text: t('breadcrumbs.home', { ns: 'translation' }), href: '/' },
              {
                text: t('breadcrumbs.volunteerActivity'),
                href: '#/',
              },
            ]}
            onFollow={(event) => {
              navigate(event.detail.href);
              event.preventDefault();
            }}
          />
        </div>
      }
      content={getContent()}
      contentType="cards"
    />
  );
}
